/**
 * What day it is, according to the phone.
 *
 * The date is worked out here, in the browser, rather than on the server. The
 * phone is in Sam's pocket and knows what day it is where she is standing; a
 * server in a container has no particular opinion, and if the two disagree,
 * food logged late at night lands on the wrong day.
 */

/** Today as YYYY-MM-DD, in whatever timezone this device is set to. */
export function today(): string {
  return toKey(new Date());
}

export function toKey(date: Date): string {
  // Built by hand from the local parts. Deliberately not toISOString(), which
  // converts to UTC first -- at 7pm in Denver that already says tomorrow.
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const dayOfMonth = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${dayOfMonth}`;
}

/** The day before or after a given one; `step` of -1 is yesterday. */
export function shift(key: string, step: number): string {
  const [year, month, dayOfMonth] = key.split("-").map(Number);
  // Months are 0-based here, and Date happily rolls over month and year ends.
  return toKey(new Date(year, month - 1, dayOfMonth + step));
}

/** "Today", "Yesterday", or something like "Sat 14 Mar". */
export function describe(key: string): string {
  if (key === today()) return "Today";
  if (key === shift(today(), -1)) return "Yesterday";
  const [year, month, dayOfMonth] = key.split("-").map(Number);
  return new Date(year, month - 1, dayOfMonth).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/** The time of day a thing was logged, as "8:15 am". */
export function clockTime(isoTimestamp: string): string {
  return new Date(isoTimestamp).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}
